import { ChangeEvent, FC, MutableRefObject } from 'react';

import {
  CrossIcon,
  FiltersIcon,
  SearchIcon
} from '@/components/ui/Icons/Icons';

import * as S from './SearchInput.styles';

type Props = {
  query?: string;
  isOpen: boolean;
  isBordered?: boolean;
  isFullWidth?: boolean;
  shouldExpand?: boolean;
  searchInputRef?: MutableRefObject<HTMLDivElement | null>;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  submitLabel?: string;
  autoFocus?: boolean;
  readonly?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onReset?: () => void;
  onFilter?: () => void;
  onSubmit?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  style?: CSSStyleRule;
};

export const SearchInput: FC<Props> = ({
  isOpen,
  isBordered = false,
  isFullWidth = false,
  shouldExpand = false,
  query,
  searchInputRef,
  inputRef,
  placeholder,
  submitLabel,
  autoFocus,
  readonly,
  onChange,
  onFocus,
  onReset,
  onFilter,
  onSubmit,
  onBlur,
  onKeyDown
}) => {
  const isClearButtonVisible = onReset && query !== '';
  return (
    <S.Wrapper
      onClick={() => inputRef?.current?.focus({ preventScroll: true })}
      data-testid="search-input-wrapper"
      $isBordered={isBordered}
      $isFullWidth={isFullWidth}
      $isOpen={isOpen}
      $shouldExpand={shouldExpand}
      ref={searchInputRef}
    >
      <S.SearchIconWrapper>
        <SearchIcon />
      </S.SearchIconWrapper>
      <S.SearchInput
        readOnly={readonly}
        type="text"
        ref={inputRef}
        placeholder={placeholder}
        value={query ?? ''}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
      />

      {isClearButtonVisible && (
        <S.ClearButton
          onClick={e => {
            e.stopPropagation();
            onReset();
          }}
          data-testid="clear-button"
        >
          <CrossIcon />
        </S.ClearButton>
      )}

      {onFilter && (
        <S.FiltersButton
          aria-label="Open filter"
          data-testid="open-filter-button"
          onClick={e => {
            e.stopPropagation();
            onFilter();
          }}
        >
          <FiltersIcon />
        </S.FiltersButton>
      )}

      {onSubmit && (
        <S.SubmitButton onClick={onSubmit} variant="brand" size="m">
          {submitLabel}
        </S.SubmitButton>
      )}
    </S.Wrapper>
  );
};
